import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableEmpty, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { fullName, apiMessage, scheduleStatusTone, useAutoRefresh } from '../lib';
import { deleteSchedule, useSchedules } from '../reportingApi';
import type { ReportSchedule } from '../reporting-types';
import { ScheduleFormDialog } from './ScheduleFormDialog';
import { ScheduleEditDialog } from './ScheduleEditDialog';
import { RecipientsDialog } from './RecipientsDialog';

function cadence(schedule: ReportSchedule, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const time = `${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')}`;
  if (schedule.frequency === 'DAILY') return t('reporting.schedules.cadenceDaily', { time });
  if (schedule.frequency === 'WEEKLY') {
    return t('reporting.schedules.cadenceWeekly', {
      day: t(`reporting.weekday.${schedule.dayOfWeek ?? 0}`),
      time,
    });
  }
  return t('reporting.schedules.cadenceMonthly', { day: schedule.dayOfMonth ?? 1, time });
}

/**
 * ADR-0013 §2.6, on screen: every schedule row says whose authority it runs
 * under, and the note above the table explains what that means in practice —
 * a re-derived permission set on every firing, never a snapshot, so an
 * operator setting one up understands why a colleague's report can quietly
 * change shape (or stop) after a role change with nobody touching this screen.
 */
export default function SchedulesTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();

  const { schedules, loading, error, reload } = useSchedules({ limit: 100 });

  // No VENUE_EVENTS for a schedule suspending itself or a run landing — see
  // `useAutoRefresh`. A schedule's `nextRunAt`/`status`/`consecutiveFailures`
  // all change on the cron's own clock, with nobody touching this screen.
  useAutoRefresh(reload, { enabled: true, intervalMs: 30_000 });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ReportSchedule | null>(null);
  const [managingRecipients, setManagingRecipients] = useState<ReportSchedule | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const remove = async (schedule: ReportSchedule) => {
    const ok = await confirm.confirm({
      title: t('reporting.schedules.confirmDeleteTitle'),
      body: t('reporting.schedules.confirmDeleteBody', { name: schedule.name }),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(schedule.id);
    try {
      await deleteSchedule(schedule.id);
      toast.success(t('reporting.schedules.deleted', { name: schedule.name }));
      reload();
    } catch (err) {
      toast.error(apiMessage(err, t('reporting.errors.deleteFailed')));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-ink-muted">{t('reporting.schedules.authorityNotice')}</p>
        {can('reports.schedule') && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Icon as={Plus} aria-hidden />
            {t('reporting.schedules.create')}
          </Button>
        )}
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !schedules.length ? (
        <EmptyState
          title={t('reporting.schedules.empty')}
          hint={t('reporting.schedules.emptyHint')}
          action={
            can('reports.schedule') && (
              <Button size="sm" onClick={() => setCreating(true)}>
                {t('reporting.schedules.create')}
              </Button>
            )
          }
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('reporting.schedules.name')}</TH>
                <TH>{t('reporting.schedules.report')}</TH>
                <TH>{t('reporting.schedules.cadence')}</TH>
                <TH>{t('reporting.schedules.nextRun')}</TH>
                <TH>{t('reporting.schedules.runsAs')}</TH>
                <TH>{t('common.status')}</TH>
                <TH className="text-end">{t('common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {schedules.map((schedule) => {
                const runner = schedule.runAsUser;
                return (
                  <TR key={schedule.id}>
                    <TD className="font-medium">{schedule.name}</TD>
                    <TD className="text-ink-muted">{schedule.savedReport.name}</TD>
                    <TD>
                      {cadence(schedule, t)}
                      {/* The hour above is wall-clock in THIS zone, not the
                          viewer's — spelling it out is what stops a Cairo
                          operator reading a Riyadh venue's "06:00" as their own. */}
                      <span className="block text-xs text-ink-muted">{schedule.timezone}</span>
                    </TD>
                    <TD className="tabular-nums">
                      {schedule.status === 'SUSPENDED' ? '—' : formatDateTime(schedule.nextRunAt)}
                    </TD>
                    <TD>
                      <span className={runner.isActive ? undefined : 'text-red-600 dark:text-red-400'}>
                        {fullName(runner)}
                      </span>
                      {!runner.isActive && (
                        <span className="block text-xs text-red-600 dark:text-red-400">
                          {t('reporting.schedules.runnerInactive')}
                        </span>
                      )}
                    </TD>
                    <TD>
                      <div className="space-y-1">
                        <Badge tone={scheduleStatusTone(schedule.status)}>
                          {t(`reporting.scheduleStatus.${schedule.status}`)}
                        </Badge>
                        {schedule.status === 'SUSPENDED' && schedule.lastError && (
                          <p className="max-w-xs text-xs text-red-600 dark:text-red-400">
                            {schedule.lastError}
                          </p>
                        )}
                        {schedule.consecutiveFailures > 0 && schedule.status !== 'SUSPENDED' && (
                          <p className="text-xs text-amber-700 dark:text-amber-400">
                            {t('reporting.schedules.consecutiveFailures', {
                              count: schedule.consecutiveFailures,
                            })}
                          </p>
                        )}
                      </div>
                    </TD>
                    <TD className="text-end">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setManagingRecipients(schedule)}
                          aria-label={t('reporting.schedules.recipients')}
                          title={t('reporting.schedules.recipientsCount', {
                            count: schedule.recipients.length,
                          })}
                          className="rounded p-1.5 text-ink-muted hover:bg-hover hover:text-ink"
                        >
                          <Icon as={Users} size="sm" />
                        </button>
                        {can('reports.schedule') && (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditing(schedule)}
                              aria-label={t('common.edit')}
                              className="rounded p-1.5 text-ink-muted hover:bg-hover hover:text-ink"
                            >
                              <Icon as={Pencil} size="sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => remove(schedule)}
                              disabled={deleting === schedule.id}
                              aria-label={t('common.delete')}
                              className="rounded p-1.5 text-ink-muted hover:bg-hover hover:text-red-600 disabled:opacity-40"
                            >
                              <Icon as={Trash2} size="sm" />
                            </button>
                          </>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
              {!schedules.length && <TableEmpty colSpan={7}>{t('reporting.schedules.empty')}</TableEmpty>}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <ScheduleFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => reload()}
      />
      <ScheduleEditDialog schedule={editing} onClose={() => setEditing(null)} onSaved={() => reload()} />
      <RecipientsDialog
        schedule={managingRecipients}
        onClose={() => setManagingRecipients(null)}
        onChanged={(updated) => {
          setManagingRecipients(updated);
          reload();
        }}
      />
    </div>
  );
}
